import { useRef } from 'react';
import { animate, useMotionValue } from 'framer-motion';

import useStateMachine from '../hooks/useStateMachine';

/**
 * An animation is something that can be started and stopped
 * like in a video player. The animation logic is useful whenever
 * something should keep moving / animating while it is in a
 * certain state (like hover).
 *
 * To animate between states see the useSteppedTransition
 * hook.
 */
export const useSteppedAnimation = ({
  steps = 0,
  maxVelocity = 12,
  slowDownDuration = 5,
  speedUpDuration = 0.25
}) => {
  const raf = useRef(null);
  const lastFrame = useRef(null);

  const progress = useMotionValue(0);
  const velocity = useMotionValue(0);

  const onFrame = (predicateFn = () => true) => {
    const delta = Date.now() - lastFrame.current;

    if (delta > 1000 / velocity.get()) {
      lastFrame.current = Date.now();
      progress.set((progress.get() + 1) % steps);
    }

    if (predicateFn()) {
      raf.current = requestAnimationFrame(() => onFrame(predicateFn));
    }
  };

  const stopFrameLoop = () => {
    if (raf.current) cancelAnimationFrame(raf.current);
  };

  const [_, send] = useStateMachine({
    initial: 'idle',
    states: {
      idle: {
        on: { TOGGLE: 'playing', START: 'playing' }
      },
      stopped: {
        on: { TOGGLE: 'playing', START: 'playing' },
        effect() {
          const minVelocity = 2;
          const controls = animate(velocity, [null, minVelocity], {
            duration: slowDownDuration,
            ease: 'linear'
          });

          onFrame(() => {
            return velocity.get() > minVelocity || progress.get() !== 0;
          });

          return () => {
            stopFrameLoop();
            controls.stop();
          };
        }
      },
      playing: {
        on: { TOGGLE: 'stopped', STOP: 'stopped' },
        effect() {
          const controls = animate(velocity, [null, maxVelocity], {
            duration: speedUpDuration,
            ease: 'linear'
          });
          onFrame();

          return () => {
            stopFrameLoop();
            controls.stop();
          };
        }
      }
    }
  });

  return [
    progress,
    {
      start: () => send('START'),
      stop: () => send('STOP'),
      toggle: () => send('TOGGLE')
    }
  ];
};
